import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, type IntegrationCard, type IntegrationsView } from '../api'
import { timeAgo } from '../time'
import { Icon } from './Icon'

/**
 * "Where your support and your issues live" — the two connection cards.
 *
 * The repository card's siblings, and they follow its shape rather than
 * inventing a second visual language: a deployment with no app says so and
 * offers nothing, a project with no connection offers one button that leaves
 * the product, every state has a sentence, and disconnecting says out loud
 * which half of it is NOT done for you.
 *
 * ---
 *
 * **Two cards for seven vendors, not seven cards.** A customer has one
 * helpdesk and one issue tracker. Seven equal boxes would be a screen that
 * cannot say what it wants, and worse: the nightly sweeps take the FIRST
 * connected vendor in their own list and never mention the second, so a person
 * who connected two would have one that quietly does nothing. So the choice is
 * offered once per kind, and once something is connected the others are put
 * away behind a sentence rather than left as six more buttons to press.
 *
 * **Every ending is a sentence.** The callback cannot render a screen — it is a
 * redirect from somebody else's host — so the reason travels on the query
 * string and is turned into words here, then removed from the address so a
 * reload does not re-announce something that happened ten minutes ago. That is
 * the Slack card's idiom, and the codes are deliberately the same shape.
 *
 * **Trello arrives differently and the difference is visible in this file.**
 * Its token comes back in the URL FRAGMENT, which no server ever receives, so
 * the screen is what reads it and hands it back over a POST. Nothing else about
 * it is special, which is why there is one effect for it and not a second card.
 */
export function ConnectedIntegrations({ mayChange }: { mayChange: boolean }) {
  const [state, setState] = useState<IntegrationsView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  /** What has been typed into a card's fields but not saved yet. */
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    try {
      const found = await api.integrations()
      setState(found)
      setDrafts((held) => {
        const next: Record<string, string> = { ...held }
        for (const card of found.integrations) {
          for (const setting of card.settings) {
            const key = `${card.vendor}.${setting.key}`
            if (next[key] === undefined) {
              const value = card.connection?.settings[setting.key]
              next[key] = typeof value === 'string' ? value : ''
            }
          }
        }
        return next
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => void load(), [load])

  /** One busy key rather than a boolean, the idiom every Settings panel uses. */
  const act = useCallback(
    async (key: string, work: () => Promise<unknown>, done?: string) => {
      setBusy(key)
      setError(null)
      setNote(null)
      try {
        await work()
        await load()
        if (done) setNote(done)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(null)
      }
    },
    [load],
  )

  /**
   * What the round trip said, read once from the address it came back on.
   *
   * Every code a callback can end with is here. A code with no sentence would
   * be a person landing on this card with nothing to read, which is the exact
   * failure this whole flow is written to avoid — so the fallback still says
   * something true rather than nothing.
   */
  useEffect(() => {
    const search = new URLSearchParams(window.location.search)
    const vendor = search.get('integration')
    const said = search.get('connected')
    if (!vendor || !said) return
    const name = LABELS[vendor] ?? 'That integration'
    const words: Record<string, string> = {
      ok: `${name} is connected. The next nightly sweep will use it.`,
      cancelled: `Nothing was connected — ${name} was not given permission.`,
      expired: 'That took too long, or the link had been changed. Press Connect again.',
      'not-allowed': `Only an owner or an admin of this workspace can connect ${name} to it.`,
      taken: `That ${name} account is already connected to another Staleless workspace. Remove it there first.`,
      refused: `${name} would not finish connecting. Nothing was changed — try again.`,
      scope: `${name} was connected without permission to read anything. Connect it again and accept the read access it asks for.`,
      'key-missing': `This installation of Staleless cannot store a ${name} credential. Tell whoever runs it.`,
      'no-code': `${name} sent us back without anything to exchange. Press Connect again.`,
      unavailable: `This installation of Staleless has no ${name} app.`,
      unnamed: `${name} answered, but did not say which account it was. Nothing was stored — try again.`,
      host: 'That is not a subdomain. Give the part before .zendesk.com and nothing else.',
    }
    const sentence = words[said] ?? `${name} did not finish connecting.`
    if (said === 'ok') setNote(sentence)
    else setError(sentence)
    const url = new URL(window.location.href)
    url.searchParams.delete('integration')
    url.searchParams.delete('connected')
    window.history.replaceState({}, '', url.toString())
  }, [])

  /**
   * Trello's half of the return trip, which no server can do.
   *
   * Trello puts the token in the FRAGMENT — `#token=…` — and a browser never
   * sends a fragment to a server, so this is the only place it exists. It is
   * handed straight back over a POST and stripped from the address in the same
   * breath, so it does not sit in the visible URL of a screen somebody may be
   * sharing.
   */
  useEffect(() => {
    const search = new URLSearchParams(window.location.search)
    if (search.get('integration') !== 'trello') return
    const stateParam = search.get('state')
    const token = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token')
    const url = new URL(window.location.href)
    url.searchParams.delete('integration')
    url.searchParams.delete('state')
    url.hash = ''
    window.history.replaceState({}, '', url.toString())
    if (!stateParam) return
    if (!token) {
      setError('Nothing was connected — Trello was not given permission.')
      return
    }
    void act(
      'trello:connect',
      () => api.connectTrello(stateParam, token),
      'Trello is connected. Give it the board to read, below.',
    )
  }, [act])

  const start = useCallback(
    (vendor: string, host?: string) =>
      act(`start:${vendor}`, async () => {
        const { url } = await api.startIntegration(vendor, host)
        window.location.href = url
      }),
    [act],
  )

  const kinds = useMemo(
    () => ({
      support: state?.integrations.filter((one) => one.kind === 'support') ?? [],
      issue: state?.integrations.filter((one) => one.kind === 'issue') ?? [],
    }),
    [state],
  )

  if (!state) {
    return (
      <section className="settings__card" data-integrations>
        <h2>Support desk</h2>
        <span className="skeleton" />
      </section>
    )
  }

  return (
    <>
      <Kind
        title="Support desk"
        blurb="Connect the helpdesk your customers write to. Staleless reads the conversations, finds the questions people keep asking, and names the page that should have answered — support volume becomes a documentation to-do list. It only ever reads."
        cards={kinds.support}
        mayChange={mayChange}
        busy={busy}
        error={error}
        note={note}
        drafts={drafts}
        setDrafts={setDrafts}
        act={act}
        start={start}
      />
      <Kind
        title="Issue tracker"
        blurb="Connect where your team plans. A page can name the issue that changed it, and when that issue ships while the page has not been touched, the page is flagged as describing something that is no longer true. It only ever reads."
        cards={kinds.issue}
        mayChange={mayChange}
        busy={busy}
        error={null}
        note={null}
        drafts={drafts}
        setDrafts={setDrafts}
        act={act}
        start={start}
      />
    </>
  )
}

/** What each vendor is called, for a message that arrives before the list does. */
const LABELS: Record<string, string> = {
  zendesk: 'Zendesk',
  intercom: 'Intercom',
  front: 'Front',
  helpscout: 'Help Scout',
  linear: 'Linear',
  jira: 'Jira',
  trello: 'Trello',
}

interface KindProps {
  title: string
  blurb: string
  cards: IntegrationCard[]
  mayChange: boolean
  busy: string | null
  error: string | null
  note: string | null
  drafts: Record<string, string>
  setDrafts: (update: (held: Record<string, string>) => Record<string, string>) => void
  act: (key: string, work: () => Promise<unknown>, done?: string) => Promise<void>
  start: (vendor: string, host?: string) => Promise<void>
}

/**
 * One card: a helpdesk, or a tracker.
 *
 * Connected first and the choice second, because once something is connected
 * the choice is no longer the question — and a card that kept offering three
 * more buttons underneath a working connection would be inviting the one
 * mistake this integration has: a second connection that quietly does nothing.
 */
function Kind({
  title,
  blurb,
  cards,
  mayChange,
  busy,
  error,
  note,
  drafts,
  setDrafts,
  act,
  start,
}: KindProps) {
  const connected = cards.filter((one) => one.connection !== null)
  const offered = cards.filter((one) => one.connection === null)

  return (
    <section
      className="settings__card"
      data-integrations={title.toLowerCase().replace(/\s+/g, '-')}
    >
      <h2>{title}</h2>
      <p className="home__quiet">{blurb}</p>

      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}
      {note && (
        <p className="screen__note">
          <Icon name="check" size={13} /> {note}
        </p>
      )}

      {connected.map((card) => (
        <Connected
          key={card.vendor}
          card={card}
          mayChange={mayChange}
          busy={busy}
          drafts={drafts}
          setDrafts={setDrafts}
          act={act}
        />
      ))}

      {connected.length === 0 &&
        offered.map((card) => (
          <Offer
            key={card.vendor}
            card={card}
            mayChange={mayChange}
            busy={busy}
            drafts={drafts}
            setDrafts={setDrafts}
            start={start}
          />
        ))}

      {connected.length > 0 && offered.length > 0 && (
        <p className="field__note" data-integrations-swap>
          Disconnect this to connect a different one. Only one is read, so two connected at once
          would leave one of them doing nothing.
        </p>
      )}
    </section>
  )
}

/**
 * One vendor that is not connected: what it needs, or the button.
 *
 * A deployment with no registration says which variables are unset. The person
 * reading their own Settings screen is often the person who can set them, and
 * "this installation has no Linear app" with no next step is a dead end.
 */
function Offer({
  card,
  mayChange,
  busy,
  drafts,
  setDrafts,
  start,
}: {
  card: IntegrationCard
  mayChange: boolean
  busy: string | null
  drafts: Record<string, string>
  setDrafts: KindProps['setDrafts']
  start: KindProps['start']
}) {
  const hostKey = `${card.vendor}.host`
  if (!card.configured) {
    return (
      <p className="field__note" data-integration-missing={card.vendor}>
        <strong>{card.label}</strong> — this installation of Staleless has no {card.label} app.
        {card.missing.length > 0 && (
          <>
            {' '}
            Set{' '}
            {card.missing.map((name, at) => (
              <span key={name}>
                {at > 0 ? ', ' : ''}
                <code>{name}</code>
              </span>
            ))}{' '}
            to offer it.
          </>
        )}
      </p>
    )
  }

  return (
    <div className="settings__block" data-integration={card.vendor}>
      <p className="home__quiet">
        <strong>{card.label}</strong> — {card.buys}
      </p>
      {card.host && (
        <div className="field">
          <label className="label" htmlFor={`integration-host-${card.vendor}`}>
            {card.host.label}
          </label>
          <input
            id={`integration-host-${card.vendor}`}
            className="input"
            value={drafts[hostKey] ?? ''}
            placeholder="acme"
            disabled={!mayChange}
            onChange={(e) => setDrafts((held) => ({ ...held, [hostKey]: e.target.value }))}
          />
          <p className="field__note">{card.host.note}</p>
        </div>
      )}
      {mayChange ? (
        <div className="settings__row">
          <button
            className="btn"
            disabled={busy !== null}
            data-integration-connect={card.vendor}
            onClick={() => void start(card.vendor, drafts[hostKey]?.trim() || undefined)}
          >
            {busy === `start:${card.vendor}` ? <Icon name="spinner" size={13} /> : null} Connect{' '}
            {card.label}
          </button>
        </div>
      ) : (
        <p className="home__quiet">An owner or an admin can connect it.</p>
      )}
      <Asks card={card} />
    </div>
  )
}

/**
 * What the consent screen is about to ask for, said before they press it.
 *
 * Not decoration. Every one of these integrations is pointed at somebody else's
 * account, and the reason an administrator refuses one is a permission list
 * they did not expect. A vendor that takes no scope parameter says where the
 * choice was actually made instead of quietly showing nothing.
 */
function Asks({ card }: { card: IntegrationCard }) {
  if (card.scopes.length > 0) {
    return (
      <p className="field__note" data-integration-scopes={card.vendor}>
        It asks for{' '}
        {card.scopes.map((scope, at) => (
          <span key={scope}>
            {at > 0 ? ', ' : ''}
            <code>{scope}</code>
          </span>
        ))}{' '}
        and nothing else. Staleless cannot change anything in your {card.label}.
      </p>
    )
  }
  return (
    <p className="field__note" data-integration-scopes={card.vendor}>
      {card.permissions ?? `Staleless cannot change anything in your ${card.label}.`}
    </p>
  )
}

/** One vendor that IS connected: to what, by whom, and how it is doing. */
function Connected({
  card,
  mayChange,
  busy,
  drafts,
  setDrafts,
  act,
}: {
  card: IntegrationCard
  mayChange: boolean
  busy: string | null
  drafts: Record<string, string>
  setDrafts: KindProps['setDrafts']
  act: KindProps['act']
}) {
  const found = card.connection!
  return (
    <div className="settings__block" data-integration={card.vendor}>
      <p className="screen__note">
        <Icon name="check" size={13} /> <strong>{card.label}</strong> is connected to{' '}
        <strong>{found.accountName ?? found.account}</strong>, by {found.connectedName}.
      </p>

      <dl className="settings__facts">
        <dt>Last worked</dt>
        <dd>{found.workedAt ? timeAgo(found.workedAt) : 'It has not been asked yet.'}</dd>
        <dt>Reads</dt>
        <dd>{found.scopes.length > 0 ? found.scopes.join(', ') : 'read only'}</dd>
      </dl>

      {/*
        The two things that make a connection look healthy and serve nothing:
        a setting it still needs, and a refusal that happened after it was set
        up. Both are said here rather than left in a log the customer cannot
        read.
      */}
      {found.problem && (
        <p className="screen__warn" data-integration-problem={card.vendor}>
          <Icon name="info" size={13} /> {found.problem}
        </p>
      )}
      {found.lastError && (
        <p className="screen__error" data-integration-error={card.vendor}>
          <Icon name="alert" size={13} /> {found.lastError}
          {found.erroredAt ? ` (${timeAgo(found.erroredAt)})` : ''}
        </p>
      )}

      {card.settings.map((setting) => {
        const key = `${card.vendor}.${setting.key}`
        return (
          <div className="field" key={setting.key}>
            <label className="label" htmlFor={`integration-${key}`}>
              {setting.label}
            </label>
            <div className="settings__row">
              <input
                id={`integration-${key}`}
                className="input"
                value={drafts[key] ?? ''}
                placeholder={setting.placeholder}
                disabled={!mayChange}
                onChange={(e) => setDrafts((held) => ({ ...held, [key]: e.target.value }))}
              />
              {mayChange && (
                <button
                  className="btn"
                  disabled={busy !== null}
                  data-integration-save={setting.key}
                  onClick={() =>
                    void act(
                      `save:${key}`,
                      () =>
                        api.setIntegrationSettings(card.vendor, found.account, {
                          [setting.key]: (drafts[key] ?? '').trim() || null,
                        }),
                      'Saved.',
                    )
                  }
                >
                  {busy === `save:${key}` ? <Icon name="spinner" size={13} /> : null} Save
                </button>
              )}
            </div>
            <p className="field__note">{setting.note}</p>
          </div>
        )
      })}

      {mayChange && (
        <div className="settings__row">
          <button
            className="btn btn--sm"
            disabled={busy !== null}
            data-integration-check={card.vendor}
            onClick={() =>
              void act(`check:${card.vendor}`, () =>
                api.checkIntegration(card.vendor, found.account),
              )
            }
          >
            {busy === `check:${card.vendor}` ? <Icon name="spinner" size={13} /> : null} Check now
          </button>
        </div>
      )}

      {mayChange && (
        <Disconnect
          busy={busy}
          card={card}
          account={found.accountName ?? found.account}
          onDisconnect={() =>
            void act(
              `disconnect:${card.vendor}`,
              () => api.disconnectIntegration(card.vendor, found.account),
              `Disconnected. ${card.leaves}`,
            )
          }
        />
      )}
    </div>
  )
}

/**
 * Disconnecting, confirmed in place.
 *
 * Inline rather than `window.confirm`, which this codebase forbids and has a
 * test to prove it. Worth confirming because it stops a signal a team may be
 * relying on — and the sentence says the part that is NOT done for them, so
 * nobody walks away believing the app is gone from their helpdesk when it is
 * still listed there.
 */
function Disconnect({
  busy,
  card,
  account,
  onDisconnect,
}: {
  busy: string | null
  card: IntegrationCard
  account: string
  onDisconnect: () => void
}) {
  const [asking, setAsking] = useState(false)
  if (!asking) {
    return (
      <div className="settings__row">
        <button
          className="btn btn--ghost btn--sm"
          disabled={busy !== null}
          data-integration-disconnect={card.vendor}
          onClick={() => setAsking(true)}
        >
          Disconnect
        </button>
      </div>
    )
  }
  return (
    <div className="pubs__confirm">
      <p className="home__quiet">
        This stops reading <strong>{account}</strong>, and deletes the credential here.{' '}
        {card.leaves}
      </p>
      <div className="pubs__actions">
        <button className="btn btn--danger btn--sm" disabled={busy !== null} onClick={onDisconnect}>
          {busy === `disconnect:${card.vendor}` ? <Icon name="spinner" size={13} /> : null}{' '}
          Disconnect
        </button>
        <button className="btn btn--ghost btn--sm" onClick={() => setAsking(false)}>
          Keep it connected
        </button>
      </div>
    </div>
  )
}
